import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import { Calendar, Clock, Users, MapPin, Briefcase } from 'lucide-react';

export default function EmployeeShiftBoard() {
  const { user } = useAuth();
  const [availableShifts, setAvailableShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const getRoleLabel = (role) => {
    const roleMap = {
      'caregiver': 'CNA',
      'dsp': 'DSP',
      'lpn': 'LPN',
      'rn': 'RN',
      'admin': 'Admin'
    };
    return roleMap[role] || role;
  };

  useEffect(() => {
    fetchAvailableShifts();
  }, []);

  const fetchAvailableShifts = async () => {
    try {
      const response = await api.get('/shifts/available');
      setAvailableShifts(response.data);
    } catch (error) {
      toast.error('Failed to load available shifts');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (shiftId) => {
    try {
      await api.post(`/shifts/${shiftId}/signup`);
      toast.success('Successfully signed up for shift!');
      fetchAvailableShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up for shift');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getSpotsRemaining = (shift) => {
    return shift.max_employees - shift.assigned_count;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-purple-900">Available Shifts</h2>
        <p className="text-gray-600 mt-1">Browse and sign up for shifts matching your role</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : availableShifts.length === 0 ? (
        <Card className="p-12 text-center border-purple-100">
          <Calendar className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-purple-900 mb-2">No available shifts</h3>
          <p className="text-gray-600">Check back later for new shift opportunities</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {availableShifts.map((shift) => (
            <Card key={shift.id} className="p-6 border-purple-100 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-purple-900 mb-1">{shift.title}</h3>
                  <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                    {getRoleLabel(shift.required_role)} Position
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-purple-900">
                    {getSpotsRemaining(shift)} {getSpotsRemaining(shift) === 1 ? 'spot' : 'spots'}
                  </div>
                  <div className="text-xs text-gray-600">available</div>
                </div>
              </div>

              {shift.description && (
                <p className="text-gray-700 mb-4">{shift.description}</p>
              )}

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">{formatDate(shift.shift_date)}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <span>{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</span>
                </div>

                {shift.location && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin className="w-5 h-5 text-purple-600" />
                    <span>{shift.location}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-gray-700">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span>{shift.assigned_count} of {shift.max_employees} positions filled</span>
                </div>
              </div>

              <Button
                onClick={() => handleSignup(shift.id)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                data-testid={`signup-shift-${shift.id}`}
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Sign Up for This Shift
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}